import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { addGroupExpense, getGroupExpenses, markSplitAsPaid } from '../../../services/groupService';
import { useAuth } from '../../../contexts/AuthContext';

const ExpenseModal = ({ isOpen, onClose, groupId, groupMembers }) => {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('add'); // 'add' or 'view'
  
  // Form state
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('food');
  const [expenseDate, setExpenseDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [splitType, setSplitType] = useState('equal'); // 'equal' or 'custom'
  const [customSplits, setCustomSplits] = useState({});

  useEffect(() => {
    if (isOpen && groupId) {
      loadExpenses();
      initializeCustomSplits();
    }
  }, [isOpen, groupId]);

  const loadExpenses = async () => {
    setIsLoading(true);
    try {
      const data = await getGroupExpenses(groupId);
      setExpenses(data);
    } catch (error) {
      console.error('Error loading expenses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const initializeCustomSplits = () => {
    let splits = {};
    groupMembers?.forEach(member => {
      splits[member?.userId] = 0;
    });
    setCustomSplits(splits);
  };

  const handleAddExpense = async () => {
    if (!description || !amount || parseFloat(amount) <= 0) {
      alert('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      const totalAmount = parseFloat(amount);
      let splits = [];

      if (splitType === 'equal') {
        const splitAmount = totalAmount / groupMembers?.length;
        splits = groupMembers?.map(member => ({
          userId: member?.userId,
          amount: splitAmount
        }));
      } else {
        splits = Object.entries(customSplits)?.filter(([_, amt]) => parseFloat(amt) > 0)?.map(([userId, amt]) => ({
            userId,
            amount: parseFloat(amt)
          }));

        const totalSplit = splits?.reduce((sum, s) => sum + s?.amount, 0);
        if (Math.abs(totalSplit - totalAmount) > 0.01) {
          alert('Split amounts must equal the total expense amount');
          setIsLoading(false);
          return;
        }
      }

      const result = await addGroupExpense(groupId, {
        description,
        amount: totalAmount,
        category,
        expenseDate,
        splits
      }, user?.id);

      if (result?.success) {
        alert('Expense added successfully!');
        resetForm();
        loadExpenses();
        setActiveTab('view');
      } else {
        alert('Failed to add expense. Please try again.');
      }
    } catch (error) {
      console.error('Error adding expense:', error);
      alert('Failed to add expense.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsPaid = async (splitId) => {
    setIsLoading(true);
    try {
      const result = await markSplitAsPaid(splitId);
      if (result?.success) {
        loadExpenses();
      } else {
        alert('Failed to mark as paid.');
      }
    } catch (error) {
      console.error('Error marking as paid:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setDescription('');
    setAmount('');
    setCategory('food');
    setExpenseDate(new Date()?.toISOString()?.split('T')?.[0]);
    setSplitType('equal');
    initializeCustomSplits();
  };

  const handleCustomSplitChange = (userId, value) => {
    setCustomSplits(prev => ({
      ...prev,
      [userId]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Group Expenses</h2>
            <p className="text-sm text-muted-foreground mt-1">Track and split costs with your group</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab('add')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'add' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Icon name="Plus" size={16} className="inline mr-2" />
            Add Expense
          </button>
          <button
            onClick={() => setActiveTab('view')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'view' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Icon name="List" size={16} className="inline mr-2" />
            View Expenses ({expenses?.length})
          </button>
        </div>

        <div className="p-6">
          {/* Add Expense Tab */}
          {activeTab === 'add' && (
            <div className="space-y-4">
              <Input
                label="Description"
                value={description}
                onChange={(e) => setDescription(e?.target?.value)}
                placeholder="e.g., Dinner at restaurant"
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Amount (₱)"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e?.target?.value)}
                  placeholder="0.00"
                  required
                />

                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e?.target?.value)}
                    className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="accommodation">Accommodation</option>
                    <option value="activity">Activity</option>
                    <option value="food">Food</option>
                    <option value="transport">Transport</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <Input
                label="Date"
                type="date"
                value={expenseDate}
                onChange={(e) => setExpenseDate(e?.target?.value)}
                required
              />

              {/* Split Type */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Split Type
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="equal"
                      checked={splitType === 'equal'}
                      onChange={(e) => setSplitType(e?.target?.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground">Split Equally</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="custom"
                      checked={splitType === 'custom'}
                      onChange={(e) => setSplitType(e?.target?.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground">Custom Split</span>
                  </label>
                </div>
              </div>

              {/* Custom Splits */}
              {splitType === 'custom' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Custom Amounts per Person
                  </label>
                  {groupMembers?.map((member) => (
                    <div key={member?.userId} className="flex items-center gap-2">
                      <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                        <Icon name="User" size={14} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">Member {member?.userId?.slice(0, 8)}</span>
                      </div>
                      <Input
                        type="number"
                        value={customSplits?.[member?.userId] || ''}
                        onChange={(e) => handleCustomSplitChange(member?.userId, e?.target?.value)}
                        placeholder="0.00"
                        className="w-32"
                      />
                    </div>
                  ))}
                </div>
              )}

              <Button
                variant="default"
                size="lg"
                fullWidth
                onClick={handleAddExpense}
                loading={isLoading}
                disabled={isLoading}
                iconName="Plus"
                iconPosition="left"
              >
                Add Expense
              </Button>
            </div>
          )}

          {/* View Expenses Tab */}
          {activeTab === 'view' && (
            <div className="space-y-4">
              {expenses?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="Receipt" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No expenses yet</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActiveTab('add')}
                    className="mt-4"
                  >
                    Add First Expense
                  </Button>
                </div>
              ) : (
                expenses?.map((expense) => (
                  <div
                    key={expense?.id}
                    className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">{expense?.description}</h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(expense?.expenseDate)?.toLocaleDateString()} • {expense?.category}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-foreground">₱{expense?.amount?.toLocaleString()}</p>
                      </div>
                    </div>

                    {/* Splits */}
                    <div className="space-y-2 mt-4 pt-4 border-t border-border">
                      <p className="text-xs font-medium text-muted-foreground mb-2">Split Details:</p>
                      {expense?.splits?.map((split) => (
                        <div
                          key={split?.id}
                          className="flex items-center justify-between text-sm"
                        >
                          <div className="flex items-center gap-2">
                            <Icon 
                              name={split?.isPaid ? 'CheckCircle' : 'Circle'} 
                              size={14} 
                              className={split?.isPaid ? 'text-success' : 'text-muted-foreground'}
                            />
                            <span className="text-foreground">
                              {split?.userId === user?.id ? 'You' : `User ${split?.userId?.slice(0, 8)}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground">
                              ₱{split?.splitAmount?.toLocaleString()}
                            </span>
                            {!split?.isPaid && split?.userId === user?.id && (
                              <Button
                                variant="outline"
                                size="xs"
                                onClick={() => handleMarkAsPaid(split?.id)}
                                disabled={isLoading}
                              >
                                Mark Paid
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpenseModal;